import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Divider,
  FormControl,
  HStack,
  Image,
  Input,
  Modal,
  Pressable,
  Select,
  Text,
  VStack,
} from 'native-base';
import { Icon } from '../../Common/components/Icon';
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook';
import { brandFromCustomerID } from '../../Common/utils/brandFromCustomerID';
import { generateCloudflareImage } from '../../DoctorMedications/const/cloudflareImage';
import currencyjs from 'currency.js';
import currency from 'currency.js';
import Loading from '../../Common/components/Loading';
import { OrderView } from '../../__generated__/graphql';
import { useDoctorCustomerMenu } from '../hooks/useDoctorCustomerMenu';
import {
  iconForShippingMethod,
  nameForShippingMethod,
  SUBSCRIPTION_SHIPPING_TYPE,
} from '../../PatientOrder/utils/shippingMethods';
import { PaymentComponent } from '../../PaymentComponent/component/PaymentComponent';
import { useDoctorCustomerPaymentMethods } from '../../DoctorCustomerPaymentMethods/hooks/useDoctorCustomerPaymentMethods';
import {
  amountDescriptionForCode,
  calculateProductDiscountFromCodes,
  orderViewToDiscountItem,
} from '../../DoctorCustomerPromoCodes/utils/benefitCodesUtils';

const Product = ({
  product,
  isSelected,
  toggleItem,
  discount,
}: {
  product: OrderView;
  isSelected: boolean;
  discount: number;
  toggleItem: (item: OrderView) => void;
}) => {
  const total = currencyjs(product?.medication?.retail ?? 0).multiply(
    product?.prescription?.qty ?? 1,
  );

  return (
    <HStack
      pb={1}
      mb={1}
      borderBottomColor={'gray.100'}
      borderBottomWidth={1}
      space={2}
      key={product?.medication?.id}
    >
      <Checkbox
        value={''}
        isDisabled={
          !['Available', 'AvailableScript'].includes(product?.status ?? 'NotAvailableNoScript')
        }
        isChecked={isSelected}
        onChange={() => toggleItem(product)}
      />

      <Image
        resizeMode={'contain'}
        w={12}
        h={12}
        source={{ uri: generateCloudflareImage(product?.medication?.image) }}
      />

      <VStack space={1} flex={1}>
        <Text fontWeight={600}>
          {product?.prescription?.qty} x {product?.medication?.name}
        </Text>

        <Text>{product?.message}</Text>

        <HStack>
          {product?.status === 'AvailableScript' && <Badge bg={'green.200'}>Available</Badge>}

          {product?.status === 'NotAvaiableRepeatInterval' && (
            <Badge bg={'orange.200'}>Refill Interval</Badge>
          )}
        </HStack>
      </VStack>

      <Text strikeThrough={discount > 0}>{total.format()}</Text>

      {discount > 0 && <Text>{total.subtract(discount).format()}</Text>}
    </HStack>
  );
};

const Menu = ({ customerID, closeModal }: { customerID: string; closeModal: () => void }) => {
  const {
    availableProducts,
    productsLoading,
    setValue,
    getDeliveryQuotes,
    order,
    placing,
    availableAccessories,
    quotesRetrieved,
    quotesLoading,
    itemSelected,
    placeOrder,
    error,
    unpaidConsults,
    paymentComponent,
    canOrder,
    deliveryCost,
    subtotal,
    total,
    toggleItem,
    toggleDiscountCode,
    discounts,
    discountCodes,
    appliedCodes,
    isSubscribeEligible,
    isSubscribing,
    onSubscribingChange
  } = useDoctorCustomerMenu(customerID, closeModal);

  const { data: cards, loading: cardsLoading } = useDoctorCustomerPaymentMethods(customerID);

  if (productsLoading || cardsLoading) return <Loading />;

  const canIOrder = canOrder();

  return (
    <>
      <Modal.Body bg={'white'} flex={1}>
        <HStack space={4}>
          <VStack flex={1} space={3}>
            <Text fontSize={'md'} fontWeight={600}>
              Medications
            </Text>

            {availableProducts?.length === 0 && <Text>No scripts available to order</Text>}

            {availableProducts?.map((a) => (
              <Product
                product={a as OrderView}
                key={a?.medication?.id}
                toggleItem={toggleItem}
                discount={
                  calculateProductDiscountFromCodes(
                    appliedCodes,
                    [orderViewToDiscountItem(a as OrderView)],
                    null,
                    null,
                    0,
                  ).total.value
                }
                isSelected={itemSelected(a as OrderView)}
              />
            ))}

            <Divider />

            <Text fontSize={'md'} fontWeight={600}>
              Accessories
            </Text>

            {availableAccessories?.map((a) => (
              <Product
                product={a as OrderView}
                key={a?.medication?.id}
                toggleItem={toggleItem}
                discount={
                  calculateProductDiscountFromCodes(
                    appliedCodes,
                    [orderViewToDiscountItem(a as OrderView)],
                    null,
                    null,
                    0,
                  ).total.value
                }
                isSelected={itemSelected(a as OrderView)}
              />
            ))}
          </VStack>

          <VStack
            h={'100%'}
            space={2}
            flex={1}
            borderLeftColor={'gray.300'}
            pl={4}
            ml={4}
            borderLeftWidth={1}
          >
            <Text fontSize={'md'} fontWeight={600}>
              Delivery
            </Text>

            <HStack space={4}>
              <VStack space={2} flex={1}>
                <FormControl>
                  <FormControl.Label>Street</FormControl.Label>

                  <Input
                    isDisabled={placing}
                    value={order.street}
                    onChangeText={(e) => setValue('street', e)}
                  />
                </FormControl>

                <FormControl flex={1}>
                  <FormControl.Label>Suburb</FormControl.Label>

                  <Input
                    value={order.suburb}
                    isDisabled={placing}
                    onChangeText={(e) => setValue('suburb', e)}
                  />
                </FormControl>

                <HStack space={2}>
                  <FormControl flex={1}>
                    <FormControl.Label>Postcode</FormControl.Label>

                    <Input
                      isDisabled={placing}
                      value={order.postcode}
                      onChangeText={(e) => setValue('postcode', e)}
                    />
                  </FormControl>

                  <FormControl flex={1}>
                    <FormControl.Label>State</FormControl.Label>

                    <Select
                      isDisabled={placing}
                      selectedValue={order.state}
                      onValueChange={(text) => setValue('state', text)}
                    >
                      {['ACT', 'NSW', 'QLD', 'SA', 'TAS', 'VIC', 'WA', 'NT'].map((state) => (
                        <Select.Item label={state} value={state} />
                      ))}
                    </Select>
                  </FormControl>
                </HStack>

                <Button variant={'outline'} onPress={getDeliveryQuotes} size={'sm'}>
                  Get Delivery Quotes
                </Button>
              </VStack>

              <VStack flex={1} space={2}>
                {!quotesLoading && !quotesRetrieved && order?.quotes?.length === 0 && (
                  <Text m={6} textAlign={'center'}>
                    Click get delivery quotes to see available delivery options
                  </Text>
                )}

                {!quotesLoading && quotesRetrieved && order?.quotes?.length === 0 && (
                  <Text>No quotes found</Text>
                )}

                {quotesLoading && <Loading />}

                {order?.quotes?.map((shippingMethod) => {
                  return (
                    <HStack
                      borderBottomColor={'gray.100'}
                      borderBottomWidth={1}
                      key={shippingMethod.type}
                      pb={1}
                      mb={1}
                      alignItems={'center'}
                      space={2}
                    >
                      <Checkbox
                        value={''}
                        isDisabled={
                          placing ||
                          (shippingMethod.type === SUBSCRIPTION_SHIPPING_TYPE && !isSubscribing)
                        }
                        onChange={() => setValue('deliveryMethod', shippingMethod?.type ?? '')}
                        isChecked={order?.deliveryMethod === shippingMethod?.type}
                      />

                      <Image source={iconForShippingMethod(shippingMethod.type)} w={6} h={6} />
                      <Text flex={1}>{nameForShippingMethod(shippingMethod.type)}</Text>
                      <Text>{currency(shippingMethod?.cost ?? 0).format()}</Text>
                    </HStack>
                  );
                })}
              </VStack>
            </HStack>

            <Divider />

            {discountCodes?.length > 0 && (
              <>
                <Text fontSize={'md'} fontWeight={600}>
                  Discount Codes
                </Text>

                <VStack mb={2} borderRadius={'sm'} borderColor={'gray.200'} borderWidth={1}>
                  {discountCodes?.map((code) => (
                    <Pressable
                      borderBottomColor={'gray.200'}
                      borderBottomWidth={1}
                      px={6}
                      py={3}
                      onPress={() => toggleDiscountCode(code)}
                    >
                      <HStack space={2}>
                        <Checkbox
                          value={''}
                          isChecked={appliedCodes.findIndex((a) => a?.code === code?.code) > -1}
                        />

                        <Text flex={1} fontWeight={600}>
                          {code?.title}
                        </Text>

                        <Text>{amountDescriptionForCode(code)}</Text>
                      </HStack>
                    </Pressable>
                  ))}
                </VStack>

                <Divider />
              </>
            )}

            <Text fontSize={'md'} fontWeight={600}>
              Payment
            </Text>

            <PaymentComponent
              total={total.value}
              brand={brandFromCustomerID(customerID)}
              allowedMethods={['card', 'inperson']}
              cardsLoading={cardsLoading}
              cards={cards}
              disabled={placing}
              ref={paymentComponent}
              setCardCompleted={(e) => {
                setValue('cardSupplied', e);
              }}
              isSubscribeEligible={isSubscribeEligible()}
              isSubscribing={isSubscribing}
              onSubscribingChange={onSubscribingChange}
            />

            {error && <Alert status={'error'}>{error}</Alert>}
          </VStack>
        </HStack>
      </Modal.Body>
      <Modal.Footer>
        <HStack space={4} alignItems={'center'}>
          <HStack space={2}>
            <Text fontSize={'md'} flex={1}>
              Subtotal:
            </Text>
            <Text fontSize={'md'}>{subtotal}</Text>
          </HStack>

          {unpaidConsults.value > 0 && (
            <HStack space={2}>
              <Text fontSize={'md'} flex={1}>
                Consult:
              </Text>
              <Text fontSize={'md'}>{unpaidConsults?.format()}</Text>
            </HStack>
          )}

          <HStack space={2}>
            <Text fontSize={'md'} flex={1}>
              Delivery:
            </Text>
            <Text fontSize={'md'}>{deliveryCost}</Text>
          </HStack>

          <HStack space={2}>
            <Text fontSize={'md'} flex={1}>
              Discounts:
            </Text>
            <Text fontSize={'md'}>{discounts}</Text>
          </HStack>

          <HStack space={2}>
            <Text fontWeight={600} fontSize={'md'} flex={1}>
              Total:
            </Text>
            <Text fontWeight={600} fontSize={'md'}>
              {total.format()}
            </Text>
          </HStack>

          <Button
            onPress={placeOrder}
            isLoading={placing}
            disabled={!canIOrder}
            opacity={canIOrder ? 1 : 0.5}
          >
            Place Order
          </Button>
        </HStack>
      </Modal.Footer>
    </>
  );
};

export function DoctorCustomerMenu({ customerID }: { customerID: string }) {
  const brand = brandFromCustomerID(customerID);

  const [show, setShow] = useState(false);

  if (brand !== 'leafdoctors') return null;

  return (
    <>
      <Button
        size={'sm'}
        leftIcon={<Icon size={'sm'} icon={faBook} />}
        variant={'outline'}
        onPress={() => setShow(true)}
      >
        Customers Products
      </Button>

      <Modal isOpen={show} size={'full'} h={'100%'} onClose={() => setShow(false)}>
        <Modal.Content bg={'white'} h={'100%'}>
          <Modal.CloseButton />
          <Modal.Header>Customers Products</Modal.Header>

          {show && <Menu customerID={customerID} closeModal={() => setShow(false)} />}
        </Modal.Content>
      </Modal>
    </>
  );
}
